import { BAR, COLOR } from "../config.ts";
import { clamp } from "../core/vec.ts";
import type { SpawnContext } from "./levelTypes.ts";

/**
 * Reusable, parametric hazard emitters. Levels compose these into timelines.
 * Every emitter telegraphs its danger — sweeping primitives enter slowly from
 * off-screen (their trajectory IS the warning), and static beams flash a
 * harmless warning outline (`warn`) before turning lethal.
 */

const OVERHANG = 60; // how far bars extend past the arena edge

/**
 * A short bar that sweeps across the arena. Unlike a full wall it spans only
 * `length` along its long axis — centered at `posFrac` of the cross axis — so
 * the player can always route around it. `axis: "h"` = a horizontal bar
 * travelling vertically; `axis: "v"` = a vertical bar travelling horizontally.
 * `speedTier` (0/1/2) selects one of the three `BAR.speeds`. `fromStart` picks
 * the entry edge.
 */
export function sweepBar(
  ctx: SpawnContext,
  axis: "h" | "v",
  posFrac: number,
  speedTier: number,
  fromStart: boolean,
  length = BAR.length,
  thickness = BAR.thickness,
): void {
  const { arenaW, arenaH } = ctx;
  const speed = BAR.speeds[((speedTier % BAR.speeds.length) + BAR.speeds.length) % BAR.speeds.length];
  const half = length / 2;
  if (axis === "h") {
    // Horizontal bar (long along X), enters from top/bottom and travels vertically.
    const cx = clamp(posFrac * arenaW, half, arenaW - half);
    const y = fromStart ? -thickness : arenaH + thickness;
    ctx.rect(cx, y, half, thickness / 2, 0, {
      vy: fromStart ? speed : -speed,
      color: COLOR.hazard,
    });
  } else {
    // Vertical bar (long along Y), enters from left/right and travels horizontally.
    const cy = clamp(posFrac * arenaH, half, arenaH - half);
    const x = fromStart ? -thickness : arenaW + thickness;
    ctx.rect(x, cy, thickness / 2, half, 0, {
      vx: fromStart ? speed : -speed,
      color: COLOR.hazard,
    });
  }
}

/**
 * A sweeping wall with a safe gap the player must line up with — the classic
 * "breeze-block" moving architecture. Built from two bar segments.
 */
export function gapWall(
  ctx: SpawnContext,
  axis: "h" | "v",
  gapCenterFrac: number,
  gapWidth: number,
  thickness: number,
  speed: number,
  fromStart: boolean,
): void {
  const { arenaW, arenaH } = ctx;
  if (axis === "h") {
    const gapC = gapCenterFrac * arenaW;
    const y = fromStart ? -thickness : arenaH + thickness;
    const vy = fromStart ? speed : -speed;
    const leftW = (gapC - gapWidth / 2 + OVERHANG) / 2;
    const rightW = (arenaW - (gapC + gapWidth / 2) + OVERHANG) / 2;
    if (leftW > 0)
      ctx.rect(leftW - OVERHANG, y, leftW, thickness / 2, 0, { vy, color: COLOR.hazard });
    if (rightW > 0)
      ctx.rect(arenaW - rightW + OVERHANG, y, rightW, thickness / 2, 0, {
        vy,
        color: COLOR.hazard,
      });
  } else {
    const gapC = gapCenterFrac * arenaH;
    const x = fromStart ? -thickness : arenaW + thickness;
    const vx = fromStart ? speed : -speed;
    const topH = (gapC - gapWidth / 2 + OVERHANG) / 2;
    const botH = (arenaH - (gapC + gapWidth / 2) + OVERHANG) / 2;
    if (topH > 0)
      ctx.rect(x, topH - OVERHANG, thickness / 2, topH, 0, { vx, color: COLOR.hazard });
    if (botH > 0)
      ctx.rect(x, arenaH - botH + OVERHANG, thickness / 2, botH, 0, {
        vx,
        color: COLOR.hazard,
      });
  }
}

/** A static beam that flashes a warning, then strikes for `life` seconds. */
export function beam(
  ctx: SpawnContext,
  x: number,
  y: number,
  hw: number,
  hh: number,
  angle: number,
  warn: number,
  life: number,
): void {
  ctx.rect(x, y, hw, hh, angle, {
    warnT: warn,
    warnTotal: warn,
    life,
    cullOffscreen: false,
    color: COLOR.hazard,
  });
}

/** A ring of bullets expanding from a point. */
export function burstRing(
  ctx: SpawnContext,
  x: number,
  y: number,
  count: number,
  speed: number,
  radius: number,
  phase = 0,
): void {
  for (let i = 0; i < count; i++) {
    const a = phase + (i / count) * Math.PI * 2;
    ctx.bullet(x, y, Math.cos(a) * speed, Math.sin(a) * speed, radius);
  }
}

/**
 * A soft-tracking shot: aims once at the player's current position (does NOT
 * home), giving the "reactive positioning" demand of Level 2 without being
 * unfair. Fires from an arena edge point.
 */
export function aimedShot(
  ctx: SpawnContext,
  x: number,
  y: number,
  speed: number,
  radius: number,
  spreadRad = 0,
): void {
  let a = Math.atan2(ctx.playerY - y, ctx.playerX - x);
  if (spreadRad) a += ctx.rng.range(-spreadRad, spreadRad);
  ctx.bullet(x, y, Math.cos(a) * speed, Math.sin(a) * speed, radius, {
    color: COLOR.hazardSoft,
  });
}

/** A fan of aimed shots, centered on the player. */
export function aimedFan(
  ctx: SpawnContext,
  x: number,
  y: number,
  count: number,
  spreadRad: number,
  speed: number,
  radius: number,
): void {
  const base = Math.atan2(ctx.playerY - y, ctx.playerX - x);
  for (let i = 0; i < count; i++) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const a = base + (t - 0.5) * spreadRad;
    ctx.bullet(x, y, Math.cos(a) * speed, Math.sin(a) * speed, radius, {
      color: COLOR.hazardSoft,
    });
  }
}

/** A diagonal stream of bullets marching across the field at a fixed angle. */
export function diagonalStream(
  ctx: SpawnContext,
  x: number,
  y: number,
  angleRad: number,
  speed: number,
  radius: number,
): void {
  ctx.bullet(x, y, Math.cos(angleRad) * speed, Math.sin(angleRad) * speed, radius, {
    color: COLOR.bullet,
  });
}
